<template>
  <div
    class="chart-legend"
    :class="{ 'chart-legend--wrapped': wrapLabels }"
  >
    <v-simple-table
      class="chart-legend__table"
      :class="{ 'chart-legend__table--wrapped': wrapLabels }"
    >
      <tbody>
        <tr
          v-for="(legend, index) in legendData"
          :key="index"
          :class="`chart-legend__row${isNotVisible(legend)}`"
          @click="toggleLegend(legend)"
          @mouseover="mouseOverLegend(legend)"
          @mouseleave="mouseLeaveLegend(legend)"
        >
          <td class="chart-legend__cell-icon">
            <v-icon
              :color="`${legend.visible? legend.color: disableColor}`"
            >
              mdi-circle-medium
            </v-icon>
          </td>
          <v-tooltip
            right
            content-class="v-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <td
                v-if="showNewStyle"
                class="chart-legend__new-cell-name"
                :class="{ 'chart-legend__new-cell-name--wrapped': wrapLabels }"
              >
                <span
                  v-bind="attrs"
                  v-on="on"
                >{{ legend.name }}</span>
              </td>
              <td
                v-else
                class="chart-legend__cell-name"
                :class="{ 'chart-legend__cell-name--wrapped': wrapLabels }"
              >
                <span
                  v-bind="attrs"
                  v-on="on"
                >{{ legend.name }}</span>
              </td>
            </template>
            <span>{{ legend.name }}</span>
          </v-tooltip>
          <td
            v-if="showLegendPercent"
            class="chart-legend__cell-percent"
          >
            {{ legend.percentage ? cellPercentage(legend.percentage) : 0 }}%
          </td>
          <td
            v-if="showInterval"
            class="chart-legend__cell-value"
          >
            {{ legend.interval }}
          </td>
          <td
            v-else-if="showLegendValue"
            class="chart-legend__cell-value"
          >
            {{ cellValue(legend.y) }}
          </td>
        </tr>
      </tbody>
    </v-simple-table>
  </div>
</template>

<script>
import { formatNumber, formatSocialNumberWithDecimal } from '@/utils/formatter';

export default {
  name: 'ChartLegendTable',
  props: {
    legendData: {
      type: [Array],
      default: () => ([]),
    },
    showLegendValue: {
      type: Boolean,
      default: false,
    },
    showLegendPercent: {
      type: Boolean,
      default: false,
    },
    showInterval: {
      type: Boolean,
      default: false,
    },
    showNewStyle: {
      type: Boolean,
      default: false,
    },
    wrapLabels: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      disableColor: 'var(--ds-color-gray-1)',
    };
  },
  methods: {
    cellValue(value) {
      return formatSocialNumberWithDecimal(value);
    },
    cellPercentage(value) {
      return formatNumber(value, { minimumFractionDigits: 0,
        maximumFractionDigits: 2 });
    },
    toggleLegend(legend) {
      legend.setVisible(!legend.visible);
    },
    isNotVisible(legend) {
      return !legend.visible ? '-disable' : '';
    },
    mouseOverLegend(legend) {
      legend.setState('hover');
    },
    mouseLeaveLegend(legend) {
      legend.setState();
    },
  },
};
</script>

<style lang="scss" scoped>
  .v-tooltip {
    position: absolute;
    margin-top: -24px;
    margin-left: -24px;
  }

  $legend-height: 72px;

  .chart-legend {
    width: auto;
    height: $legend-height;
    overflow-y: auto;

    &--wrapped {
      width: 100%;
    }

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      border: none;
      background-color: var(--ds-color-gray-1);
    }

    &__table {
      font-size: var(--ds-font-size-xs);
      font-weight: var(--ds-font-weight-semibold);
      color: var(--ds-color-text-2) !important;
    }

    &__row {
      border-bottom: none !important;

      &:hover {
        cursor: pointer;
        background-color: transparent !important;
        color: var(--ds-color-text-1) !important;
      }
    }

    &__row-disable {
      color: var(--ds-color-gray-3);

      &:hover {
        cursor: pointer;
        background-color: transparent !important;
        color: var(--ds-color-text-1) !important;
      }
    }

    &__cell-icon {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-quark) 0 0 !important;
      width: 24px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
      margin: auto 0 auto 0;
    }

    &__cell-name {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-nano) 0 0 !important;
      max-width: 120px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
      margin: auto 0 auto 0;
    }

    &__new-cell-name {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-nano) 0 0 !important;
      max-width: 120px;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
    }

    &__cell-name--wrapped,
    &__new-cell-name--wrapped {
      width: 100% !important;
      max-width: none !important;
      white-space: normal !important;
      overflow: visible !important;
      overflow-wrap: anywhere;
      text-overflow: clip !important;
    }

    &__cell-percent {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-nano) 0 0 !important;
      text-overflow: ellipsis;
      text-align: right;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
      margin: auto 0 auto 0;
    }

    &__cell-value {
      height: auto !important;
      border-bottom: none !important;
      padding: 0 var(--ds-spacing-stack-sm) 0 0 !important;
      text-align: right;
      font-size: var(--ds-font-size-xs) !important;
      line-height: var(--ds-font-line-height-slim);
      margin: auto 0 auto 0;
    }
  }

  ::v-deep {
    .v-data-table {
      width: auto !important;
    }

    .v-data-table > .v-data-table__wrapper {
      width: auto !important;
    }

    .v-data-table > .v-data-table__wrapper > table {
      width: auto;
    }

    .chart-legend__table--wrapped,
    .chart-legend__table--wrapped > .v-data-table__wrapper {
      width: 100% !important;
    }

    .chart-legend__table--wrapped > .v-data-table__wrapper > table {
      width: 100%;
      table-layout: fixed;
    }

    .theme--dark.v-data-table {
      background-color: transparent !important;
    }

    .theme--light.v-data-table {
      background-color: transparent !important;
    }
  }
</style>
